import React, { useState } from 'react'
import { ActionButton, Stack, TextField } from 'office-ui-fabric-react'

// Marks a draft that does not parse, so saving is blocked. Never persisted: the
// dialog runs getErrorCode() before every mutation.
export const INVALID_AD_FRAME_OPTIONS = 'adFrameOptionsInvalid'

export function FixedPositionForm({
  integrationTypeConfig,
  onIntegrationTypeConfigChange,
  disabled,
  errorCode,
  isAdmin,
}: {
  integrationTypeConfig: any
  onIntegrationTypeConfigChange: (config: any) => void
  disabled: boolean
  errorCode: string
  isAdmin: boolean
}) {
  return (
    <>
      <TextField
        label="Fixed Position"
        placeholder="#caroda-outstream"
        errorMessage={errorCode === 'invalidSelector' ? 'Invalid selector' : ''}
        value={integrationTypeConfig.selector || ''}
        autoComplete="off"
        readOnly={false}
        disabled={disabled}
        onChange={(
          _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
          newValue?: string
        ): void => {
          onIntegrationTypeConfigChange({
            ...integrationTypeConfig,
            selector: newValue || '',
          })
        }}
      />
      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <TextField
          label="Width"
          placeholder="640"
          type="number"
          errorMessage={
            errorCode === 'invalidWidthHeight'
              ? 'Both width and height must be provided'
              : ''
          }
          value={
            integrationTypeConfig.width != null
              ? String(integrationTypeConfig.width)
              : ''
          }
          autoComplete="off"
          disabled={disabled}
          onChange={(
            _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string
          ): void => {
            const parsed =
              newValue != null && newValue !== ''
                ? parseInt(newValue, 10)
                : undefined
            onIntegrationTypeConfigChange({
              ...integrationTypeConfig,
              width: parsed != null && !isNaN(parsed) ? parsed : undefined,
            })
          }}
        />
        <TextField
          label="Height"
          placeholder="480"
          type="number"
          errorMessage={
            errorCode === 'invalidWidthHeight'
              ? 'Both width and height must be provided'
              : ''
          }
          value={
            integrationTypeConfig.height != null
              ? String(integrationTypeConfig.height)
              : ''
          }
          autoComplete="off"
          disabled={disabled}
          onChange={(
            _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string
          ): void => {
            const parsed =
              newValue != null && newValue !== ''
                ? parseInt(newValue, 10)
                : undefined
            onIntegrationTypeConfigChange({
              ...integrationTypeConfig,
              height: parsed != null && !isNaN(parsed) ? parsed : undefined,
            })
          }}
        />
      </Stack>
      <TextField
        label="Custom CSS"
        placeholder=".carodaContainer { float: none; }"
        value={
          integrationTypeConfig.playerOptions
            ? integrationTypeConfig.playerOptions.css || ''
            : ''
        }
        autoComplete="off"
        readOnly={false}
        disabled={disabled}
        multiline={true}
        rows={5}
        onChange={(
          _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
          newValue?: string
        ): void => {
          onIntegrationTypeConfigChange({
            ...integrationTypeConfig,
            playerOptions: {
              ...(integrationTypeConfig.playerOptions || {}),
              css: newValue || '',
            },
          })
        }}
      />
      {isAdmin ? (
        <AdFrameOptionsField
          integrationTypeConfig={integrationTypeConfig}
          onIntegrationTypeConfigChange={onIntegrationTypeConfigChange}
          disabled={disabled}
        />
      ) : null}
    </>
  )
}

export function AdFrameOptionsField({
  integrationTypeConfig,
  onIntegrationTypeConfigChange,
  disabled,
}: {
  integrationTypeConfig: any
  onIntegrationTypeConfigChange: (config: any) => void
  disabled: boolean
}) {
  const stored = integrationTypeConfig.playerOptions
    ? integrationTypeConfig.playerOptions.adFrameOptions
    : undefined
  const [isOpen, setIsOpen] = useState(!!stored)
  const [draft, setDraft] = useState(
    stored ? JSON.stringify(stored, null, 2) : ''
  )
  const isInvalid = !!integrationTypeConfig[INVALID_AD_FRAME_OPTIONS]

  const onDraftChange = (text: string): void => {
    setDraft(text)
    const config = { ...integrationTypeConfig }
    delete config[INVALID_AD_FRAME_OPTIONS]
    const playerOptions = { ...(config.playerOptions || {}) }
    const parsed = parseOptions(text)
    if (parsed === undefined) {
      onIntegrationTypeConfigChange({
        ...config,
        [INVALID_AD_FRAME_OPTIONS]: true,
      })
      return
    }
    if (parsed === null) {
      delete playerOptions.adFrameOptions
    } else {
      playerOptions.adFrameOptions = parsed
    }
    onIntegrationTypeConfigChange({ ...config, playerOptions })
  }

  return (
    <Stack>
      <ActionButton
        iconProps={{ iconName: isOpen ? 'ChevronDown' : 'ChevronRight' }}
        text="Advanced ad frame options (admins)"
        disabled={disabled}
        onClick={(): void => setIsOpen(!isOpen)}
      />
      {isOpen ? (
        <TextField
          placeholder={'{\n  "hidePlaybackToggle": false\n}'}
          description="Passed straight to the ad frame. Keys are not validated."
          errorMessage={isInvalid ? 'Invalid JSON object' : ''}
          value={draft}
          autoComplete="off"
          disabled={disabled}
          multiline={true}
          rows={5}
          onChange={(
            _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string
          ): void => {
            onDraftChange(newValue || '')
          }}
        />
      ) : null}
    </Stack>
  )
}

// undefined when the text does not parse to a JSON object, null when it is blank
function parseOptions(text: string): any {
  if (!text.trim()) {
    return null
  }
  let parsed
  try {
    parsed = JSON.parse(text)
  } catch (_error) {
    return undefined
  }
  const isObject =
    parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
  return isObject ? parsed : undefined
}
